import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { toast } from 'react-toastify';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import { 
    FaUser, 
    FaPencilAlt, 
    FaCheck, 
    FaTimes, 
    FaCrown, 
    FaCalendar, 
    FaShieldAlt, 
    FaStar, 
    FaCreditCard, 
    FaLock, 
    FaArrowRight, 
    FaInfoCircle 
} from 'react-icons/fa';

import './style.css';

const ProfilePage = () => {
    const { user, updateUser } = useAuth();
    const { subscription, hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription();
    const [isEditing, setIsEditing] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [formData, setFormData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || ''
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            updateUser({ ...user, ...formData });
            toast.success('Profile updated successfully!');
            setIsEditing(false);
        } catch (error) {
            toast.error('Failed to update profile');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    return (
        <div className="profile-page">
            <InternalNavbar />
            
            <div className="profile-container">
                <div className="profile-wrapper">
                    {/* Hero Section */}
                    <div className="profile-hero">
                        <div className="hero-content">
                            <div className="hero-text">
                                <h1>My Profile</h1>
                                <p>Manage your account information and preferences</p>
                            </div>
                            <div className="hero-avatar">
                                {user?.profile_picture ? (
                                    <img 
                                        src={user.profile_picture} 
                                        alt={user.first_name}
                                        className="avatar-image"
                                    />
                                ) : (
                                    <div className="avatar-placeholder">
                                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Profile Cards Grid */}
                    <div className="profile-grid">
                        {/* Personal Information Card */}
                        <div className="profile-card main-card">
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="title-icon">
                                        <FaUser />
                                    </div>
                                    <h2>Personal Information</h2>
                                </div>
                                {!isEditing && (
                                    <button 
                                        className="edit-btn"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <FaPencilAlt />
                                        Edit
                                    </button>
                                )}
                            </div>

                            <div className="card-content">
                                {!isEditing ? (
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>First Name</label>
                                            <p>{user?.first_name || 'Not provided'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Last Name</label>
                                            <p>{user?.last_name || 'Not provided'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Email Address</label>
                                            <p>{user?.email}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Account Status</label>
                                            <div className="status-container">
                                                <span className={`status-badge ${user?.is_verified ? 'verified' : 'unverified'}`}>
                                                    <i className={`ti-${user?.is_verified ? 'check' : 'time'}`}></i>
                                                    {user?.is_verified ? 'Verified' : 'Unverified'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="edit-form">
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label>First Name</label>
                                                <input
                                                    type="text"
                                                    name="first_name"
                                                    value={formData.first_name}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="Enter your first name"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Last Name</label>
                                                <input
                                                    type="text"
                                                    name="last_name"
                                                    value={formData.last_name}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="Enter your last name"
                                                />
                                            </div>
                                            <div className="form-group full-width">
                                                <label>Email Address</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    disabled
                                                />
                                                <small>Email address cannot be changed</small>
                                            </div>
                                        </div>
                                        <div className="form-actions">
                                            <button type="submit" className="save-btn">
                                                <FaCheck />
                                                Save Changes
                                            </button>
                                            <button 
                                                type="button" 
                                                className="cancel-btn"
                                                onClick={() => setIsEditing(false)}
                                            >
                                                <FaTimes />
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Account Overview Card */}
                        <div className="profile-card">
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="title-icon purple">
                                        <FaCrown />
                                    </div>
                                    <h2>Account Overview</h2>
                                </div>
                            </div>
                            <div className="card-content">
                                <div className="overview-stats">
                                    <div className="stat-item">
                                        <div className="stat-icon">
                                            <FaCalendar />
                                        </div>
                                        <div className="stat-info">
                                            <span className="stat-label">Member Since</span>
                                            <span className="stat-value">{formatDate(user?.created_at)}</span>
                                        </div>
                                    </div>
                                    <div className="stat-item">
                                        <div className="stat-icon">
                                            <FaShieldAlt />
                                        </div>
                                        <div className="stat-info">
                                            <span className="stat-label">Account Type</span>
                                            <span className="stat-value">
                                                {hasActiveSubscription ? 
                                                    (isDemoSubscription ? 'Demo User' : 'Premium User') : 
                                                    'Free User'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                    <div className="stat-item">
                                        <div className="stat-icon">
                                            <FaStar />
                                        </div>
                                        <div className="stat-info">
                                            <span className="stat-label">Status</span>
                                            <span className={`stat-value ${user?.is_verified ? 'verified' : 'unverified'}`}>
                                                {user?.is_verified ? 'Verified' : 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Subscription Status Card */}
                        <div className="profile-card">
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="title-icon pink">
                                        <FaCreditCard />
                                    </div>
                                    <h2>Subscription Status</h2>
                                </div>
                            </div>
                            <div className="card-content">
                                {hasActiveSubscription ? (
                                    <div className="subscription-info">
                                        <div className="subscription-badge">
                                            <span className={`badge ${isDemoSubscription ? 'demo' : 'premium'}`}>
                                                {isDemoSubscription ? 'Demo Access' : 'Premium Active'}
                                            </span>
                                        </div>
                                        <div className="subscription-details">
                                            <div className="detail-row">
                                                <span>Plan Type</span>
                                                <span>{isDemoSubscription ? 'Demo' : 'Premium'}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span>Status</span>
                                                <span className="status-active">Active</span>
                                            </div>
                                            {subscription?.end_date && (
                                                <div className="detail-row">
                                                    <span>Expires</span>
                                                    <span>{formatDate(subscription.end_date)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-subscription">
                                        <div className="no-sub-icon">
                                            <FaLock />
                                        </div>
                                        <p>No active subscription</p>
                                        <a href="/subscription" className="upgrade-link">
                                            View Plans <FaArrowRight />
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Security Settings Card */}
                        <div className="profile-card">
                            <div className="card-header">
                                <div className="card-title">
                                    <div className="title-icon green">
                                        <FaLock />
                                    </div>
                                    <h2>Security Settings</h2>
                                </div>
                            </div>
                            <div className="card-content">
                                <div className="security-options">
                                    <div className="security-item">
                                        <div className="security-info">
                                            <h4>Password</h4>
                                            <p>Keep your account secure</p>
                                        </div>
                                        <button 
                                            className="security-btn"
                                            onClick={() => setIsChangePasswordOpen(true)}
                                        >
                                            Change Password
                                        </button>
                                    </div>
                                    <div className="security-item">
                                        <div className="security-info">
                                            <h4>Two-Factor Authentication</h4>
                                            <p>Add an extra layer of security</p>
                                        </div>
                                        <button className="security-btn" disabled>
                                            Enable 2FA
                                        </button>
                                    </div>
                                </div>
                                <div className="coming-soon-notice">
                                    <FaInfoCircle />
                                    <span>Two-Factor Authentication coming soon</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
            <Scrollbar />
            
            {/* Change Password Modal */}
            <ChangePasswordModal 
                isOpen={isChangePasswordOpen}
                onClose={() => setIsChangePasswordOpen(false)}
            />
        </div>
    );
};

export default ProfilePage;
